import { CompanyField } from "@/components/CompanyField";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Loader2, Landmark, Banknote, Pencil, ArrowLeftRight } from "lucide-react";
import { api } from "@/lib/api";
import { usePermissions } from "@/lib/permissions";
import { useCompany } from "@/context/CompanyContext";
import { PageHeader } from "@/components/PageHeader";
import { EmptyState } from "@/components/EmptyState";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "@/components/ui/table";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { FieldError } from "@/components/FieldError";
import { getErrorMessage, getFieldErrors } from "@/lib/errors";

const BURGUNDY = "bg-[#910029] text-white hover:bg-[#7A0022]";
const money = (v) => { const n = Number(v || 0); return `${n < 0 ? "-" : ""}₹${Math.abs(n).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`; };
const mask = (n) => (n ? `•••• ${String(n).slice(-4)}` : "—");

export default function BankAccounts() {
  const { can } = usePermissions();
  const { companies, singleCompany } = useCompany() || {};
  const canCreateBank = can("BANK_ACCOUNTS", "create");
  const canEditBank = can("BANK_ACCOUNTS", "edit");
  const [banks, setBanks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [creating, setCreating] = useState(false);
  const [transferring, setTransferring] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/masters/bank-accounts");
      setBanks(data);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const toggle = async (b) => {
    try {
      await api.patch(`/masters/bank-accounts/${b.id}`, { is_active: !b.is_active });
      load();
    } catch (err) {
      toast.error(getErrorMessage(err));
    }
  };
  const companyName = (id) => companies?.find((c) => c.id === id)?.name || "—";
  const total = banks.filter((b) => b.is_active).reduce((s, b) => s + Number(b.balance || 0), 0);

  return (
    <div className="space-y-6" data-testid="bank-accounts-page">
      <PageHeader
        title="Bank Accounts"
        description="Every bank account and cash box your company pays from or receives into. Each one keeps its own ledger balance."
        action={canCreateBank && (
          <>
          {banks.filter((b) => b.is_active).length > 1 && (
            <Button variant="outline" data-testid="transfer-button" onClick={() => setTransferring(true)} className="h-11 gap-2 rounded-lg px-5 text-sm font-semibold">
              <ArrowLeftRight className="h-4 w-4" /> Transfer
            </Button>
          )}
          <Button data-testid="create-bank-button" onClick={() => setCreating(true)} className={`h-11 gap-2 rounded-lg px-5 text-sm font-semibold shadow-sm ${BURGUNDY}`}>
            <Plus className="h-4 w-4" /> New bank account
          </Button>
          </>
        )}
      />

      {banks.length > 0 && (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
          <div className="rounded-xl border border-border bg-card p-4">
            <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Active accounts</p>
            <p className="mt-1 text-2xl font-bold text-foreground">{banks.filter((b) => b.is_active).length}</p>
          </div>
          <div className="rounded-xl border border-border bg-card p-4 sm:col-span-2">
            <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Combined balance (active)</p>
            <p className="mt-1 text-2xl font-bold tabular-nums text-foreground" data-testid="bank-total">{money(total)}</p>
          </div>
        </div>
      )}

      <div className="overflow-x-auto rounded-xl border border-border bg-card">
        {!loading && banks.length === 0 ? (
          <EmptyState title="No bank accounts yet" testId="bank-empty-state" />
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Account</TableHead><TableHead>Bank</TableHead><TableHead>Number</TableHead><TableHead>IFSC</TableHead>
                {!singleCompany && <TableHead>Company</TableHead>}<TableHead>Ledger</TableHead><TableHead className="text-right">Balance</TableHead><TableHead>Status</TableHead>
                {canEditBank && <TableHead className="w-10" />}
              </TableRow>
            </TableHeader>
            <TableBody>
              {banks.map((b) => (
                <TableRow key={b.id} data-testid={`bank-row-${b.id}`}>
                  <TableCell className="font-medium text-foreground">
                    <span className="inline-flex items-center gap-2">
                      {b.kind === "CASH" ? <Banknote className="h-4 w-4 text-[#910029]" /> : <Landmark className="h-4 w-4 text-[#910029]" />}
                      {b.name}
                    </span>
                  </TableCell>
                  <TableCell>{b.bank_name || "—"}</TableCell>
                  <TableCell className="font-mono text-xs">{mask(b.account_number)}</TableCell>
                  <TableCell className="font-mono text-xs">{b.ifsc || "—"}</TableCell>
                  {!singleCompany && <TableCell>{companyName(b.company_id)}</TableCell>}
                  <TableCell className="font-mono text-xs">{b.gl_code}</TableCell>
                  <TableCell className="text-right font-semibold tabular-nums">{money(b.balance)}</TableCell>
                  <TableCell>
                    <Switch checked={b.is_active} disabled={!canEditBank} onCheckedChange={() => toggle(b)} data-testid={`bank-active-toggle-${b.id}`} />
                  </TableCell>
                  {canEditBank && (
                    <TableCell>
                      <Button variant="ghost" size="icon" onClick={() => setEditing(b)} aria-label="Edit bank account" data-testid={`bank-edit-${b.id}`}><Pencil className="h-4 w-4" /></Button>
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>
      <p className="text-xs text-muted-foreground">
        A negative balance means money has gone out of that account with no opening balance entered. Payments made before you added accounts stay under the general "Cash &amp; Bank" ledger.
      </p>

      <TransferDialog open={transferring} onOpenChange={setTransferring} banks={banks.filter((b) => b.is_active)} onSaved={load} />
      <BankDialog open={creating} onOpenChange={setCreating} companies={companies} onSaved={load} />
      <BankDialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)} bank={editing} companies={companies} onSaved={load} />
    </div>
  );
}

function BankDialog({ open, onOpenChange, bank, companies, onSaved }) {
  const isEdit = !!bank;
  const blank = { company_id: "", kind: "BANK", name: "", bank_name: "", account_number: "", ifsc: "", opening_balance: "", opening_date: new Date().toISOString().slice(0, 10) };
  const [form, setForm] = useState(blank);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setErrors({});
    setForm(bank ? { ...blank, ...bank, bank_name: bank.bank_name || "", account_number: bank.account_number || "", ifsc: bank.ifsc || "" } : blank);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, bank]);

  const isCash = (isEdit ? bank?.kind : form.kind) === "CASH";
  const set = (f) => (e) => setForm({ ...form, [f]: e.target.value });
  const nullable = (v) => (v && v.trim() ? v.trim() : null);

  const submit = async (e) => {
    e.preventDefault();
    setErrors({});
    setSaving(true);
    try {
      if (isEdit) {
        await api.patch(`/masters/bank-accounts/${bank.id}`, {
          name: form.name, ...(isCash ? {} : { bank_name: nullable(form.bank_name), account_number: nullable(form.account_number), ifsc: nullable(form.ifsc) }),
        });
        toast.success("Bank account updated");
      } else {
        await api.post("/masters/bank-accounts", {
          company_id: form.company_id, kind: form.kind, name: form.name,
          ...(isCash ? {} : { bank_name: nullable(form.bank_name), account_number: nullable(form.account_number), ifsc: nullable(form.ifsc) }),
          opening_balance: Number(form.opening_balance) > 0 ? form.opening_balance : "0", opening_date: form.opening_date || null,
        });
        toast.success(`${form.name} added`);
      }
      onOpenChange(false);
      onSaved?.();
    } catch (err) {
      setErrors(getFieldErrors(err));
      toast.error(getErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader><DialogTitle>{isEdit ? "Edit bank account" : "New bank account"}</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-3" data-testid="bank-form">
          {!isEdit && (
            <div className="grid grid-cols-2 gap-3">
              <div>
                <CompanyField label="Company" testId="bank-company-select" value={form.company_id} onChange={(v) => setForm({ ...form, company_id: v })} />
                <FieldError error={errors.company_id} />
              </div>
              <div>
                <Label>Type</Label>
                <Select value={form.kind} onValueChange={(v) => setForm({ ...form, kind: v })}>
                  <SelectTrigger data-testid="bank-kind-select" className="mt-1.5"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="BANK">Bank account</SelectItem>
                    <SelectItem value="CASH">Cash in hand</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}
          <div>
            <Label>{isCash ? "Cash account name" : "Name shown on payments"}</Label>
            <Input required data-testid="bank-name-input" value={form.name} onChange={set("name")} className="mt-1.5" placeholder={isCash ? "e.g. Site cash box" : "e.g. HDFC Current A/c"} />
            <FieldError error={errors.name} />
          </div>
          {!isCash && (
            <>
          <div>
            <Label>Bank</Label>
            <Input data-testid="bank-bankname-input" value={form.bank_name} onChange={set("bank_name")} className="mt-1.5" placeholder="HDFC Bank, Nagercoil" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Account number</Label><Input data-testid="bank-number-input" value={form.account_number} onChange={set("account_number")} className="mt-1.5" /></div>
            <div><Label>IFSC</Label><Input data-testid="bank-ifsc-input" value={form.ifsc} onChange={set("ifsc")} className="mt-1.5 uppercase" /></div>
          </div>
            </>
          )}
          {!isEdit && (
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>{isCash ? "Cash counted (₹)" : "Opening balance (₹)"}</Label>
                <Input type="number" min="0" step="0.01" data-testid="bank-opening-input" value={form.opening_balance} onChange={set("opening_balance")} className="mt-1.5" placeholder={isCash ? "Cash in hand now" : "Money already in it"} />
                <FieldError error={errors.opening_balance} />
              </div>
              <div><Label>As on</Label><Input type="date" data-testid="bank-opening-date" value={form.opening_date} onChange={set("opening_date")} className="mt-1.5" /></div>
            </div>
          )}
          <DialogFooter>
            <Button type="submit" disabled={saving} className={BURGUNDY} data-testid="bank-submit-button">
              {saving && <Loader2 className="h-4 w-4 animate-spin" />} {isEdit ? "Save" : "Create"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function TransferDialog({ open, onOpenChange, banks, onSaved }) {
  const blank = { from: "", to: "", amount: "", date: new Date().toISOString().slice(0, 10), narration: "" };
  const [form, setForm] = useState(blank);
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (open) setForm(blank); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [open]);

  const from = banks.find((b) => b.id === form.from);
  const targets = banks.filter((b) => b.id !== form.from && (!from || b.company_id === from.company_id));

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/masters/bank-accounts/transfer", {
        from_account_id: form.from, to_account_id: form.to, amount: form.amount,
        transfer_date: form.date, narration: form.narration.trim() || null,
      });
      toast.success("Transfer recorded");
      onOpenChange(false);
      onSaved?.();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader><DialogTitle>Transfer between accounts</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-3" data-testid="transfer-form">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>From</Label>
              <Select required value={form.from} onValueChange={(v) => setForm({ ...form, from: v, to: "" })}>
                <SelectTrigger data-testid="transfer-from-select" className="mt-1.5"><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent>{banks.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>To</Label>
              <Select required value={form.to} onValueChange={(v) => setForm({ ...form, to: v })}>
                <SelectTrigger data-testid="transfer-to-select" className="mt-1.5"><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent>{targets.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Amount (₹)</Label><Input required type="number" min="0.01" step="0.01" data-testid="transfer-amount-input" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} className="mt-1.5" /></div>
            <div><Label>Date</Label><Input required type="date" data-testid="transfer-date-input" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="mt-1.5" /></div>
          </div>
          <div><Label>Note</Label><Input data-testid="transfer-note-input" value={form.narration} onChange={(e) => setForm({ ...form, narration: e.target.value })} className="mt-1.5" placeholder="Cash deposited to bank, UTR…" /></div>
          <DialogFooter>
            <Button type="submit" disabled={saving || !form.from || !form.to} className={BURGUNDY} data-testid="transfer-submit-button">
              {saving && <Loader2 className="h-4 w-4 animate-spin" />} Transfer
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
